import {
  PAIR_TOKEN_REFRESH_MS,
  RealtimeEventTag,
  type PairAcceptedPayload,
} from '@trames/shared/schemas';
import { type Client } from 'colyseus.js';
import Phaser from 'phaser';

import { createSession } from '../net/api-client.js';
import { renderQrDataUrl } from '../ui/qr-display.js';

/**
 * LobbyScene (TKT-007).
 *
 * Pantalla del Coro:
 *   - QR escaneable + room code en monospace ≥25vh (ADR-0005 §3.5).
 *   - URL legible debajo como fallback.
 *   - Refresca cada PAIR_TOKEN_REFRESH_MS (30 s) — nuevo sessionId/QR.
 *   - Cuando el móvil se enlaza, muestra "Coro de 1 jugador" con alias.
 *
 * Render minimalista en Phaser DOM elements + un canvas para el QR.
 * No simula nada: el estado autoritativo viene del server-game (ADR-0002).
 */

interface SessionViewState {
  sessionId: string;
  roomCode: string;
  qrPayload: string;
  expiresAt: number;
  browserSessionToken: string;
}

export class LobbyScene extends Phaser.Scene {
  static readonly KEY = 'LobbyScene';

  private current?: SessionViewState;
  private refreshTimer: number | undefined;
  private retryTimer: number | undefined;
  /** Marca de "ya no estoy viva, no actualices nada". */
  private stopped = false;
  private roomCodeText?: Phaser.GameObjects.Text;
  private qrImage?: Phaser.GameObjects.DOMElement;
  private enterButton?: Phaser.GameObjects.DOMElement;
  private statusText?: Phaser.GameObjects.Text;
  private hintText?: Phaser.GameObjects.Text;
  private colyseus?: Client;

  constructor() {
    super(LobbyScene.KEY);
  }

  create(): void {
    const { width, height } = this.scale;

    // Fondo cinemático placeholder (TKT futuro: optimizar a WebP).
    if (this.textures.exists('bg-lobby')) {
      const bg = this.add.image(width / 2, height / 2, 'bg-lobby');
      bg.setDisplaySize(width, height);
      bg.setDepth(-10);
      bg.setAlpha(0.55);
    }

    this.statusText = this.add
      .text(width / 2, height * 0.08, 'Escanea el código con el Juramentario', {
        fontFamily: 'ui-monospace, monospace',
        fontSize: '32px',
        color: '#f4ecd8',
      })
      .setOrigin(0.5);

    this.roomCodeText = this.add
      .text(width / 2, height * 0.55, '....', {
        fontFamily: 'ui-monospace, monospace',
        fontSize: `${Math.floor(height * 0.25)}px`,
        color: '#f4ecd8',
        letterSpacing: 12,
      } as unknown as Phaser.Types.GameObjects.Text.TextStyle)
      .setOrigin(0.5);

    this.hintText = this.add
      .text(width / 2, height * 0.85, '', {
        fontFamily: 'ui-monospace, monospace',
        fontSize: '18px',
        color: '#a39780',
      })
      .setOrigin(0.5);

    const enter = document.createElement('button');
    enter.type = 'button';
    enter.textContent = 'Entrar al Archivo';
    enter.dataset['testid'] = 'pc-enter-game';
    enter.style.padding = '14px 22px';
    enter.style.border = '1px solid #f4ecd8';
    enter.style.background = '#14161f';
    enter.style.color = '#f4ecd8';
    enter.style.font = '600 18px ui-sans-serif, system-ui, sans-serif';
    enter.style.cursor = 'pointer';
    enter.style.display = 'none';
    enter.addEventListener('click', () => this.startGameScene());
    this.enterButton = this.add.dom(width / 2, height * 0.94, enter).setOrigin(0.5);

    void this.refresh();
  }

  shutdown(): void {
    this.stopped = true;
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = undefined;
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
    }
    this.enterButton?.destroy();
  }

  private async refresh(): Promise<void> {
    if (this.stopped) return;
    try {
      const session = await createSession();
      if (this.stopped) return;
      this.current = {
        sessionId: session.sessionId,
        roomCode: session.roomCode,
        qrPayload: session.qrPayload,
        expiresAt: session.expiresAt,
        browserSessionToken: session.browserSessionToken,
      };
      this.applyCurrent();
      this.scheduleRefresh();
      void this.bindLobbyRoom();
    } catch (err) {
      if (this.stopped) return;
      this.statusText?.setText('No se pudo crear la sala. Reintentando…');

      console.warn('[LobbyScene] createSession failed', err);
      this.retryTimer = window.setTimeout(() => void this.refresh(), 3_000);
    }
  }

  /**
   * Lanza la GameScene cuando hay un browserSessionToken disponible.
   * El test e2e también puede llamarlo via window.__trames__.
   */
  startGameScene(): void {
    if (!this.current) return;
    // Mata timers antes de transicionar para que la promesa pendiente del
    // QR/setText no choque con un Text destruido por Phaser.
    this.shutdown();
    this.scene.start('GameScene', { browserSessionToken: this.current.browserSessionToken });
  }

  private async applyCurrent(): Promise<void> {
    if (this.stopped || !this.current) return;
    this.roomCodeText?.setText(this.current.roomCode);
    this.hintText?.setText(this.current.qrPayload);
    const button = this.enterButton?.node as HTMLButtonElement | undefined;
    if (button) button.style.display = 'inline-flex';

    const dataUrl = await renderQrDataUrl(this.current.qrPayload);
    if (this.stopped) return;
    const size = Math.floor(this.scale.height * 0.32);
    if (this.qrImage) this.qrImage.destroy();
    const img = document.createElement('img');
    img.src = dataUrl;
    img.width = size;
    img.height = size;
    img.alt = 'Código QR de emparejamiento';
    img.dataset['qrPayload'] = this.current.qrPayload; // hook para Playwright (TKT-010).
    this.qrImage = this.add.dom(this.scale.width / 2, this.scale.height * 0.28, img);
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    this.refreshTimer = window.setInterval(() => void this.refresh(), PAIR_TOKEN_REFRESH_MS);
  }

  private async bindLobbyRoom(): Promise<void> {
    // En esta fase el PC abre LobbyRoom por matchmaking joinOrCreate con la misma
    // sessionId; ambos roles (pc/mobile) se unen al room que comparte sessionId.
    // La conexión PC del LobbyRoom va aquí; la del móvil va en TKT-009.
    // En spike 1 omitimos abrir socket hasta tener pairSessionToken: el PC solo
    // necesita ESCUCHAR el broadcast pair.accepted, que veremos a través de
    // un canal lateral en spike 1 (polling /v1/pair/state) — fuera del scope
    // estricto de TKT-007. En su lugar mostramos un placeholder.
    this.statusText?.setText('Escanea el código con el Juramentario');
    void this.colyseus;
  }

  /** API de prueba (E2E) para inyectar un evento de pair.accepted sintético. */

  acceptPairForTesting(payload: PairAcceptedPayload): void {
    void RealtimeEventTag;
    this.statusText?.setText(`Coro de 1 jugador — ${payload.alias}`);
    // Auto-transición a GameScene tras pareado (TKT-019).
    window.setTimeout(() => this.startGameScene(), 500);
  }
}
